import { AI_REQUEST_TIMEOUT_MS, apiClient } from './api';
import { z } from 'zod';
import { GenerateNarrativeResponseSchema } from '@contract/criterionNarrative';
import {
  AuditSessionSchema,
  AuditSessionCreatedSchema,
  AuditSessionLockedEnvelope,
} from '@contract/auditSession';

// Types

// REV-945: derived from the contract revenant-api's capture test checks its
// responses against, so this cannot drift from what the API sends. It already
// had once (REV-929: created_at/updated_at declared, createdAt/updatedAt sent).
// POST returns the freshly-inserted row, which lacks finalized_at, locked_at,
// locked_by and deletedAt until it is read back: hence a separate type.
export type AuditSession = z.infer<typeof AuditSessionSchema>;
export type CreatedAuditSession = z.infer<typeof AuditSessionCreatedSchema>;
/** What locking returns: the audit and a summary of its snapshot, not the whole snapshot. */
export type LockAuditResult = z.infer<typeof AuditSessionLockedEnvelope>['data'];

export interface EvidenceSummary {
  total: number;
  applicable: number;
  pass_count: number;
  fail_count: number;
  na_count: number;
  not_tested_count: number;
  /** REV-1072: engine results for auto_assisted rules awaiting confirmation. */
  needs_review_count?: number;
  critical_path_fail_count: number;
  fail_rate: number | null;
  coverage_percent: number;
  source_mix?: Record<string, number>;
  source_methods_count?: number;
}

export interface CriterionEvaluation {
  id: string;
  audit_session_id: string;
  wcag_sc_id: string;
  wcag_level: 'A' | 'AA' | 'AAA';
  evidence_summary: EvidenceSummary | null;
  suggested_level: string;
  suggestion_flags: string[] | null;
  attested_level: string | null;
  attestation_note: string | null;
  /**
   * REV-1074: the AI proxy call that drafted the note (null for a note a
   * person wrote), and whether the reviewer edited that draft.
   */
  attestation_note_ai_call_id?: string | null;
  attestation_note_ai_edited?: boolean | null;
  attestor_user_id: string | null;
  attestation_tier: string | null;
  attested_at: string | null;
}

// REV-204: evidence-methodology shape (tools, techniques, AT, steps)
export interface Methodology {
  tools?: string[];
  techniques?: string[];
  at_used?: string[];
  steps_to_reproduce?: string[];
}

/**
 * The verdicts a PERSON can record (REV-1072).
 *
 * Narrower than TestEvaluation['verdict'] on purpose: 'needs_review' is the
 * engine's holding state and the API refuses it on write, because a reviewer
 * saying "needs review" would be attesting to nothing. 'not_tested' stays
 * recordable — the API accepts it and it means "I looked and did not test
 * this". Exported so the verdict form cannot widen it back by casting.
 */
export type RecordableVerdict = 'pass' | 'fail' | 'not_applicable' | 'not_tested';

export interface TestEvaluation {
  id: string;
  audit_session_id: string;
  test_id: string;
  content_category: string;
  check_type: 'automatic' | 'auto_assisted' | 'manual' | 'ai_assisted';
  // 'needs_review' (REV-1072) is a verdict a row can HOLD — the engine
  // produced a result for an auto_assisted rule and no person has confirmed
  // it. It is deliberately absent from recordVerdict's data below: the API
  // refuses it, because a reviewer saying "needs review" would be attesting to
  // nothing.
  verdict: 'pass' | 'fail' | 'not_applicable' | 'not_tested' | 'needs_review';
  verdict_note: string | null;
  wcag_criteria: string[];
  population_severity: Record<string, number> | null;
  impact_statement: string | null;
  is_critical_path: boolean;
  methodology: Methodology | null;
  reviewer_user_id: string | null;
  reviewed_at: string | null;
}

export interface CriterionDetailResponse extends CriterionEvaluation {
  test_evaluations: TestEvaluation[];
}

export interface ConformanceSnapshot {
  id: string;
  context_id: string;
  audit_session_id: string;
  wcag_version: string;
  target_level: string;
  snapshot_data: CriterionEvaluation[];
  summary: Record<string, unknown>;
  locked_at: string;
  locked_by: string;
  content_hash: string;
}

// REV-202: unified findings view (auto + manual) for an audit
export type FindingSource = 'auto' | 'manual';
export type FindingSeverity = 'critical' | 'major' | 'minor' | 'info';

export interface Finding {
  id: string;
  source: FindingSource;
  severity: FindingSeverity;
  rule_id: string | null;
  rule_name: string | null;
  wcag_criteria: string[];
  status: string;
  element_selector: string | null;
  element_html: string | null;
  occurrence_count: number;
  // REV-133: org-wide responses annotate each row with its context; audit-
  // scoped responses leave these undefined. REV-821: the org list sends null
  // where there is none, as its contract (@contract/findingsList) records.
  context_id?: string | null;
  context_name?: string | null;
  scan_run_id?: string | null;
  target_id?: string | null;
  /**
   * REV-720: the page this finding was found on, or null when there is no page
   * to name. Two rows reach null — a manual finding has no target at all, and
   * an automated one outlives the soft-deletion of its Target — so anything
   * grouping or labelling by this needs a bucket for it.
   */
  target_url?: string | null;
  first_detected_at?: string | null;
  audit_session_id?: string;
  audit_session_name?: string | null;
  scope_item_id?: string | null;
  verdict_note?: string | null;
  methodology?: Record<string, unknown> | null;
  is_critical_path?: boolean;
  reviewed_at?: string | null;
  raw_id: string;
}

// Service
export const auditService = {
  // Audit Sessions
  async createAudit(
    orgId: string,
    contextId: string,
    data: {
      name?: string;
      wcag_version?: string;
      target_level?: string;
      description?: string;
      testing_environment?: Record<string, unknown>;
    }
  ) {
    const res = await apiClient.post<{ success: boolean; data: CreatedAuditSession }>(
      `/v1/organizations/${orgId}/contexts/${contextId}/audits`,
      data
    );
    return res.data;
  },

  async listAudits(
    orgId: string,
    contextId: string,
    params?: {
      status?: string;
      limit?: number;
      offset?: number;
    }
  ) {
    const res = await apiClient.get<{
      success: boolean;
      data: AuditSession[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/v1/organizations/${orgId}/contexts/${contextId}/audits`, params);
    // REV-929: rows AND pagination. Returning res.data alone dropped the total,
    // so a caller could not tell a full page from the whole list.
    return { data: res.data, pagination: res.pagination };
  },

  async getAudit(orgId: string, auditId: string) {
    const res = await apiClient.get<{ success: boolean; data: AuditSession }>(
      `/v1/organizations/${orgId}/audits/${auditId}`
    );
    return res.data;
  },

  async finalizeAudit(orgId: string, auditId: string) {
    const res = await apiClient.post<{ success: boolean; data: AuditSession }>(
      `/v1/organizations/${orgId}/audits/${auditId}/finalize`
    );
    return res.data;
  },

  // REV-353: locking is gated when any criterion is at attestation tier
  // "unverified" (Tier 0). Pass waiveTierCheck=true with a justification
  // (>=20 chars, recorded in the audit log) to override.
  async lockAudit(
    orgId: string,
    auditId: string,
    waiveTierCheck = false,
    waiveTierJustification?: string
  ) {
    const body: { waive_tier_check: boolean; waive_tier_justification?: string } = {
      waive_tier_check: waiveTierCheck,
    };
    if (waiveTierJustification !== undefined) {
      body.waive_tier_justification = waiveTierJustification;
    }
    const res = await apiClient.post<{
      success: boolean;
      data: LockAuditResult;
    }>(`/v1/organizations/${orgId}/audits/${auditId}/lock`, body);
    return res.data;
  },

  // REV-206: reopen a finalized audit back to in_progress
  async reopenAudit(orgId: string, auditId: string, reason?: string) {
    const res = await apiClient.post<{ success: boolean; data: AuditSession }>(
      `/v1/organizations/${orgId}/audits/${auditId}/reopen`,
      reason ? { reason } : {}
    );
    return res.data;
  },

  // Criterion Evaluations
  async listCriteria(
    orgId: string,
    auditId: string,
    params?: {
      status?: 'needs_review' | 'attested' | 'all';
      suggested_level?: string;
      has_flag?: string;
      limit?: number;
      offset?: number;
    }
  ) {
    const res = await apiClient.get<{
      success: boolean;
      data: CriterionEvaluation[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/v1/organizations/${orgId}/audits/${auditId}/criteria`, params);
    return res;
  },

  async getCriterion(orgId: string, auditId: string, scId: string) {
    const res = await apiClient.get<{
      success: boolean;
      data: CriterionDetailResponse;
    }>(`/v1/organizations/${orgId}/audits/${auditId}/criteria/${scId}`);
    return res.data;
  },

  async attestCriterion(
    orgId: string,
    auditId: string,
    scId: string,
    data: { level: string; tier: string; note?: string; noteAiCallId?: string }
  ) {
    const res = await apiClient.post<{ success: boolean; data: CriterionEvaluation }>(
      `/v1/organizations/${orgId}/audits/${auditId}/criteria/${scId}/attest`,
      data
    );
    return res.data;
  },

  /**
   * REV-356: ask the platform AI proxy to draft a VPAT remark for this
   * criterion. Returns the draft only — the reviewer edits and persists
   * via attestCriterion.
   *
   * Response is validated against GenerateNarrativeResponseSchema so a
   * backend shape change surfaces as a clear contract error rather than
   * a silently-empty UI.
   */
  async generateNarrative(
    orgId: string,
    auditId: string,
    scId: string
  ): Promise<{ narrative: string; callId: string }> {
    const raw = await apiClient.post<unknown>(
      `/v1/organizations/${orgId}/audits/${auditId}/criteria/${scId}/generate-narrative`,
      {},
      // REV-973: through the AI proxy, so wait past the API's provider deadline.
      { timeout: AI_REQUEST_TIMEOUT_MS }
    );
    const parsed = GenerateNarrativeResponseSchema.parse(raw);
    return { narrative: parsed.data.narrative, callId: parsed.data.call_id };
  },

  // Test Evaluations
  async listTestEvaluations(
    orgId: string,
    auditId: string,
    params?: {
      verdict?: string;
      category?: string;
      check_type?: string;
      limit?: number;
      offset?: number;
    }
  ) {
    const res = await apiClient.get<{
      success: boolean;
      data: TestEvaluation[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/v1/organizations/${orgId}/audits/${auditId}/test-evaluations`, params);
    return res.data;
  },

  async recordVerdict(
    orgId: string,
    testEvalId: string,
    data: {
      verdict: RecordableVerdict;
      verdict_note?: string;
      evidence?: Record<string, unknown>;
      population_severity_override?: Record<string, number>;
      is_critical_path?: boolean;
      methodology?: Methodology | null;
    }
  ) {
    const res = await apiClient.patch<{ success: boolean; data: TestEvaluation }>(
      `/v1/organizations/${orgId}/test-evaluations/${testEvalId}/verdict`,
      data
    );
    return res.data;
  },

  // Snapshots
  async getSnapshot(orgId: string, snapshotId: string) {
    const res = await apiClient.get<{ success: boolean; data: ConformanceSnapshot }>(
      `/v1/organizations/${orgId}/snapshots/${snapshotId}`
    );
    return res.data;
  },

  // REV-353: acknowledgeUnverified appends acknowledge_unverified=true so the
  // backend's tier-0 export gate lets a snapshot containing unverified criteria
  // through. Only the VPAT formats are gated (the audit report is not), so the
  // flag lives on the VPAT URL builders only.
  getVpatExportUrl(orgId: string, snapshotId: string, acknowledgeUnverified = false) {
    const url = `/api/v1/organizations/${orgId}/snapshots/${snapshotId}/export?format=vpat-2.5`;
    return acknowledgeUnverified ? `${url}&acknowledge_unverified=true` : url;
  },

  // REV-357: VPAT 2.5 in DOCX format. Same data as the HTML export, but
  // delivered as a Word file for procurement teams that require .docx.
  getVpatDocxExportUrl(orgId: string, snapshotId: string, acknowledgeUnverified = false) {
    const url = `/api/v1/organizations/${orgId}/snapshots/${snapshotId}/export?format=vpat-2.5-docx`;
    return acknowledgeUnverified ? `${url}&acknowledge_unverified=true` : url;
  },

  // REV-207: formal audit report HTML export URL
  getAuditReportUrl(orgId: string, snapshotId: string) {
    return `/api/v1/organizations/${orgId}/snapshots/${snapshotId}/export?format=audit-report-html`;
  },

  // REV-202: unified findings view
  async listFindings(
    orgId: string,
    auditId: string,
    params?: {
      source?: 'auto' | 'manual' | 'all';
      severity?: 'critical' | 'major' | 'minor' | 'info';
      wcag_sc_id?: string;
      limit?: number;
      offset?: number;
    }
  ) {
    const res = await apiClient.get<{
      success: boolean;
      data: Finding[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/v1/organizations/${orgId}/audits/${auditId}/findings`, params);
    return res;
  },

  // REV-216: benchmark an audit against a baseline (previous audit or
  // platform-wide industry aggregate).
  async getBenchmark(
    orgId: string,
    auditId: string,
    baseline?: 'auto' | 'industry' | string | null
  ): Promise<BenchmarkResponse> {
    const params = baseline ? { baseline } : undefined;
    const res = await apiClient.get<{ success: boolean; data: BenchmarkResponse }>(
      `/v1/organizations/${orgId}/audits/${auditId}/benchmark`,
      params
    );
    return res.data;
  },
};

// REV-216: benchmark types.
export type BenchmarkConformanceLevel =
  | 'supports'
  | 'partially_supports'
  | 'does_not_support'
  | 'not_applicable'
  | 'not_evaluated';

export interface BenchmarkCounts {
  supports: number;
  partially_supports: number;
  does_not_support: number;
  not_applicable: number;
  not_evaluated: number;
  total_applicable: number;
}

export type BenchmarkByPrinciple = Record<string, Record<BenchmarkConformanceLevel, number>>;

export interface BenchmarkDelta {
  wcag_sc_id: string;
  from: BenchmarkConformanceLevel;
  to: BenchmarkConformanceLevel;
}

export interface BenchmarkBaseline {
  kind: 'previous_audit' | 'industry';
  audit_id: string | null;
  audit_name: string | null;
  finalized_at: string | null;
  wcag_version: string;
  target_level: string;
  pass_rate: number | null;
  counts_by_level: BenchmarkCounts;
  by_principle: BenchmarkByPrinciple;
  sample_size?: number;
  deltas: {
    improved: BenchmarkDelta[];
    regressed: BenchmarkDelta[];
    unchanged: number;
  } | null;
}

export interface BenchmarkResponse {
  audit: {
    id: string;
    name: string | null;
    wcag_version: string;
    target_level: string;
    status: string;
    finalized_at: string | null;
    locked_at: string | null;
  };
  counts_by_level: BenchmarkCounts;
  pass_rate: number | null;
  by_principle: BenchmarkByPrinciple;
  baseline: BenchmarkBaseline | null;
}
